import React, { useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  QuestionAnswer as QuestionIcon,
  Quiz as ExamIcon,
  People as PeopleIcon,
  Analytics as AnalyticsIcon,
  AccountCircle,
  Logout,
  Settings,
  VerifiedUser as VerifyIcon,
  Flag as FlagIcon,
  ImportExport as ImportExportIcon,
  LocalOffer as LocalOfferIcon,
  LibraryBooks as SubjectIcon,
  Search as SearchIcon,
  NotificationsOutlined as NotificationsIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const drawerWidth = 260;

interface LayoutProps {
  children: React.ReactNode;
}

interface MenuItemSpec {
  text: string;
  icon: React.ReactNode;
  path: string;
  section?: string;
  badge?: number;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { admin, logout } = useAuth();

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);
  const handleProfileMenuClose = () => setAnchorEl(null);
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const menuItems: MenuItemSpec[] = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard', section: 'Overview' },
    { text: 'Analytics', icon: <AnalyticsIcon />, path: '/analytics', section: 'Overview' },

    { text: 'Subjects', icon: <SubjectIcon />, path: '/subjects', section: 'Content' },
    { text: 'Questions', icon: <QuestionIcon />, path: '/questions', section: 'Content' },
    { text: 'Import/Export', icon: <ImportExportIcon />, path: '/questions/import-export', section: 'Content' },
    { text: 'Review Queue', icon: <VerifyIcon />, path: '/questions/review', section: 'Content' },
    { text: 'Question Reports', icon: <FlagIcon />, path: '/question-reports', section: 'Content' },
    { text: 'Exams', icon: <ExamIcon />, path: '/exams', section: 'Content' },

    { text: 'Users', icon: <PeopleIcon />, path: '/users', section: 'Users' },
    { text: 'Referral Codes', icon: <LocalOfferIcon />, path: '/referral-codes', section: 'Users' },
  ];

  // Group menu items by section preserving order
  const groupedMenu = menuItems.reduce<Record<string, MenuItemSpec[]>>((acc, item) => {
    const section = item.section ?? 'Other';
    (acc[section] ??= []).push(item);
    return acc;
  }, {});

  const sectionOrder = ['Overview', 'Content', 'Users'];
  const sortedSections = sectionOrder.filter((s) => groupedMenu[s]);

  const drawer = (
    <Box
      sx={{
        height: '100%',
        background: 'linear-gradient(180deg, #338275 0%, #1F5449 100%)',
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Brand */}
      <Box
        sx={{
          py: 3,
          px: 2,
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            bgcolor: 'white',
            px: 2,
            py: 1,
            borderRadius: 2.5,
            boxShadow: '0 6px 18px rgba(0,0,0,0.18)',
          }}
        >
          <Box
            sx={{
              width: 32, height: 32, borderRadius: 1.5,
              background: 'linear-gradient(135deg, #338275 0%, #1F5449 100%)',
              color: 'white', fontWeight: 800, fontSize: 18,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            E
          </Box>
          <Typography sx={{ fontWeight: 800, fontSize: 16, color: '#1A3A34' }}>
            EduNxt<Box component="span" sx={{ color: '#D4A017' }}>AI</Box>
          </Typography>
        </Box>
      </Box>

      {/* Menu */}
      <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
        {sortedSections.map((section) => (
          <Box key={section}>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                px: 2.5,
                pt: 2,
                pb: 0.75,
                color: 'rgba(255,255,255,0.5)',
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                fontSize: 10,
              }}
            >
              {section}
            </Typography>
            <List disablePadding>
              {groupedMenu[section].map((item) => {
                const selected = location.pathname === item.path;
                return (
                  <ListItem key={item.text} disablePadding sx={{ px: 1.5 }}>
                    <ListItemButton
                      selected={selected}
                      onClick={() => {
                        navigate(item.path);
                        if (mobileOpen) setMobileOpen(false);
                      }}
                      sx={{
                        borderRadius: 2,
                        my: 0.25,
                        color: 'rgba(255,255,255,0.8)',
                        '& .MuiListItemIcon-root': {
                          color: 'rgba(255,255,255,0.8)',
                          minWidth: 34,
                        },
                        '&:hover': {
                          bgcolor: 'rgba(255,255,255,0.08)',
                          color: 'white',
                          '& .MuiListItemIcon-root': { color: 'white' },
                        },
                        '&.Mui-selected': {
                          bgcolor: 'rgba(255,255,255,0.15)',
                          color: 'white',
                          fontWeight: 700,
                          '& .MuiListItemIcon-root': { color: 'white' },
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                        },
                      }}
                    >
                      <ListItemIcon>{item.icon}</ListItemIcon>
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{
                          fontSize: 13,
                          fontWeight: selected ? 700 : 500,
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        ))}
      </Box>

      {/* User foot */}
      <Box sx={{ p: 1.5 }}>
        <Box
          sx={{
            p: 1.25,
            borderRadius: 2,
            bgcolor: 'rgba(255,255,255,0.08)',
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
          }}
        >
          <Avatar sx={{ width: 32, height: 32, bgcolor: '#D4A017', color: '#4A3600', fontWeight: 800 }}>
            {admin?.name?.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'white' }} noWrap>
              {admin?.name ?? 'Admin'}
            </Typography>
            <Typography
              sx={{ fontSize: 10, color: 'rgba(255,255,255,0.7)', textTransform: 'capitalize' }}
              noWrap
            >
              {admin?.role?.replace('_', ' ') ?? 'Admin'}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', backgroundColor: '#FAF8F2', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          backgroundColor: 'white',
          color: 'text.primary',
          borderBottom: '1px solid #F0F2F1',
          backgroundImage: 'none',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Box
            sx={{
              flex: 1,
              maxWidth: 420,
              mr: 'auto',
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'center',
              gap: 1,
              bgcolor: '#F5F7F6',
              borderRadius: 2,
              px: 1.5,
              py: 1,
            }}
          >
            <SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} />
            <Typography variant="body2" sx={{ color: 'text.disabled' }}>
              Search questions, students, schools…
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              sx={{
                bgcolor: '#F5F7F6',
                borderRadius: 2,
                '&:hover': { bgcolor: '#EBEEED' },
              }}
              size="small"
            >
              <NotificationsIcon />
            </IconButton>
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                flexDirection: 'column',
                alignItems: 'flex-end',
                ml: 1,
              }}
            >
              <Typography variant="body2" fontWeight={700}>
                {admin?.name}
              </Typography>
              <Typography
                variant="caption"
                sx={{ textTransform: 'capitalize', color: 'text.secondary' }}
              >
                {admin?.role?.replace('_', ' ')}
              </Typography>
            </Box>
            <IconButton
              onClick={handleProfileMenuOpen}
              size="small"
              sx={{ ml: 1 }}
              aria-controls={anchorEl ? 'account-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={anchorEl ? 'true' : undefined}
            >
              <Avatar sx={{ width: 36, height: 36, bgcolor: '#D4A017', color: '#4A3600', fontWeight: 800 }}>
                {admin?.name?.charAt(0).toUpperCase()}
              </Avatar>
            </IconButton>
          </Box>

          <Menu
            anchorEl={anchorEl}
            id="account-menu"
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            onClick={handleProfileMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            slotProps={{
              paper: {
                elevation: 0,
                sx: {
                  mt: 1.5,
                  borderRadius: 2,
                  border: '1px solid #F0F2F1',
                  boxShadow: '0 8px 24px rgba(51, 130, 117, 0.10)',
                  minWidth: 180,
                },
              },
            }}
          >
            <MenuItem onClick={() => navigate('/profile')}>
              <ListItemIcon><AccountCircle fontSize="small" /></ListItemIcon>
              Profile
            </MenuItem>
            <MenuItem onClick={() => navigate('/settings')}>
              <ListItemIcon><Settings fontSize="small" /></ListItemIcon>
              Settings
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
              <ListItemIcon sx={{ color: 'error.main' }}><Logout fontSize="small" /></ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="admin navigation"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              border: 'none',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              border: 'none',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          backgroundColor: '#FAF8F2',
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
